/**
 * Guard para estilos `:hover` en dispositivos táctiles.
 *
 * ## El problema
 *
 * En una pantalla táctil el navegador aplica `:hover` al elemento que hay bajo
 * el dedo en el `touchstart`, y **no tiene ningún evento con el que quitarlo**:
 * el dedo se levanta, no "sale" del elemento como haría un ratón. Así que el
 * resaltado se queda pegado hasta que se toca otra cosa.
 *
 * En un desplegable eso es especialmente confuso, porque el `:hover` de una
 * opción es visualmente idéntico al de la opción seleccionada: el operario ve
 * dos filas marcadas y no sabe cuál eligió. Se nota más dentro del APK porque
 * el WebView no se cierra nunca — no hay recarga que limpie el estado.
 *
 * ## La solución
 *
 * Las *interaction media features* de Media Queries L4 permiten preguntar por
 * la capacidad real del puntero. Envolviendo el `:hover` en esta consulta, el
 * estilo ni siquiera se declara en un dispositivo sin ratón, así que no hay
 * nada que se pueda quedar pegado. Es el remedio estándar (CSS-Tricks,
 * "Solving Sticky Hover States with @media (hover: hover)").
 *
 * - `hover: hover`  → el dispositivo puede mantener el puntero encima.
 * - `pointer: fine` → el puntero primario es preciso (cursor, no un dedo).
 *
 * Se piden las dos, que es el par inverso exacto del
 * `@media (hover: none) and (pointer: coarse)` que ya se usa en
 * `HelpTooltip` y `TruncatedText` para agrandar el área de toque. Un portátil
 * con pantalla táctil y ratón, o un iPad con trackpad, siguen contando como
 * "hover capaz" — su puntero primario es fino — así que no se pierde nada en
 * híbridos.
 *
 * ## Uso
 *
 * ```ts
 * import { hoverCapable } from '../../../styles/hover';
 *
 * export const ListItem = styled.li`
 *   ${hoverCapable} {
 *     &:hover {
 *       background-color: ${({ theme }) => theme.colors.backgroundLila};
 *     }
 *   }
 * `;
 * ```
 *
 * ⚠️ El `:hover` va **dentro** de la media query, no al revés.
 *
 * Nota para los E2E: Playwright corre Chromium de escritorio, que sí cumple
 * `(hover: hover) and (pointer: fine)`, así que los estilos de hover siguen
 * aplicándose en los tests igual que antes.
 */
export const hoverCapable = '@media (hover: hover) and (pointer: fine)';
